import React from 'react';

import { SimpleObject } from '../entities/simple-object';
import { fromJsDate } from '../services/formatter/date';

/**
 * Parses manual date input string into a date range
 * Supports formats: "2025-01-01 - 2025-01-31", "2025-01-01 to 2025-01-31", "2025-01-01 ~ 2025-01-31"
 * Also supports single date input: "2025-01-01"
 */
export const parseManualDateInput = (value: string): [Date | null, Date | null] => {
  if (!value || value.trim() === '') {
    return [null, null];
  }

  // Handle date range input (e.g., "2025-01-01 - 2025-01-31" or "2025-01-01 to 2025-01-31")
  const rangeSeparators = [' - ', ' to ', ' ~ '];
  let startDate: Date | null = null;
  let endDate: Date | null = null;

  for (const separator of rangeSeparators) {
    if (value.includes(separator)) {
      const [start, end] = value.split(separator);

      startDate = start.trim() ? new Date(start.trim()) : null;
      endDate = end.trim() ? new Date(end.trim()) : null;
      break;
    }
  }

  // If no separator found, try to parse as single date
  if (startDate === null && endDate === null) {
    const singleDate = new Date(value.trim());

    if (!Number.isNaN(singleDate.getTime())) {
      startDate = singleDate;
    }
  }

  // Validate dates
  if (startDate && Number.isNaN(startDate.getTime())) startDate = null;
  if (endDate && Number.isNaN(endDate.getTime())) endDate = null;

  return [startDate, endDate];
};

/**
 * Creates a key down handler for date input fields that validates on Enter key press
 * @param startFieldName - Name of the start date field in the filter state
 * @param endFieldName - Name of the end date field in the filter state
 * @param dateValue - Current date values state
 * @param filterState - Current filter state
 * @param setDateValue - State setter for date values
 * @param onUpdate - Callback to update the filter state
 * @returns Event handler function for onKeyDown
 */
export const createDateKeyDownHandler = <T extends object>(
  startFieldName: string,
  endFieldName: string,
  dateValue: SimpleObject<Date | null>,
  filterState: T,
  setDateValue: React.Dispatch<React.SetStateAction<SimpleObject<Date | null>>>,
  onUpdate: (filters: T) => void,
) => {
  return (event: React.KeyboardEvent<HTMLInputElement | HTMLDivElement>) => {
    if (event.key === 'Enter') {
      const target = event.currentTarget as HTMLInputElement;
      const { value } = target;

      // Parse the manual input
      const [start, end] = parseManualDateInput(value);

      setDateValue({ ...dateValue, [startFieldName]: start, [endFieldName]: end });
      onUpdate({
        ...filterState,
        [startFieldName]: start ? fromJsDate(start) : null,
        [endFieldName]: end ? fromJsDate(end) : null,
      });
    }
  };
};
